import { DisplayObject, Point } from "pixi.js";
import { gsap } from "gsap";
type Timeline = gsap.core.Timeline;

export class ZoomCamera {
    private static readonly ZOOM_DURATION = 1;

    private readonly targetDefaultPosition: Point;
    private readonly targetDefaultScale: Point;
    private readonly areaSize: CameraAreaSize;
    private readonly zoomPower: number;
    private readonly target: DisplayObject;
    private zoomAnimation: Timeline;

    public constructor(
        target: DisplayObject,
        zoomPower: number = 2
    ) {
        this.target = target;
        this.areaSize = { width: target.getLocalBounds().width, height: target.getLocalBounds().height };
        this.targetDefaultPosition = new Point(target.x, target.y);
        this.targetDefaultScale = new Point(target.scale.x, target.scale.y);
        this.zoomPower = zoomPower;
        this.zoomAnimation = new gsap.core.Timeline();
    }

    public async zoomIn(zoomToTarget: Point): Promise<void> {
        this.reset();
        const moveToPosition = this.getMoveToPointWithZoom(zoomToTarget);
        this.zoomAnimation.to(this.target, { x: moveToPosition.x, y: moveToPosition.y, delay: 0.2, duration: ZoomCamera.ZOOM_DURATION, ease: "power1.inOut" });
        this.zoomAnimation.to(this.target.scale, { x: this.target.scale.x * this.zoomPower, y: this.target.scale.y * this.zoomPower, duration: ZoomCamera.ZOOM_DURATION, ease: "power1.inOut" }, "<");
        await this.zoomAnimation.play();
    }

    public async zoomOut(): Promise<void> {
        this.reset();
        this.zoomAnimation.to(this.target, { x: this.targetDefaultPosition.x, y: this.targetDefaultPosition.y, duration: ZoomCamera.ZOOM_DURATION, ease: "power1.inOut" });
        this.zoomAnimation.to(this.target.scale, { x: this.targetDefaultScale.x, y: this.targetDefaultScale.y, duration: ZoomCamera.ZOOM_DURATION, ease: "power1.inOut" }, "<");
        await this.zoomAnimation.play();
    }

    private reset(): void {
        this.zoomAnimation.pause();
        this.zoomAnimation.clear();
    }

    private getMoveToPointWithZoom(zoomToTarget: Point): Point {
        const maxPosX = this.areaSize.width - this.zoomPower * this.areaSize.width;
        let posX = this.areaSize.width * 0.5 - (zoomToTarget.x + this.targetDefaultPosition.x) * this.zoomPower;
        posX = this.target.x - posX < 0 ? 0 :
            posX < maxPosX ? maxPosX : posX;

        const maxPosY = this.areaSize.height - this.zoomPower * this.areaSize.height;
        let posY = this.areaSize.height * 0.5 - (zoomToTarget.y + this.targetDefaultPosition.y) * this.zoomPower;
        posY = this.target.y - posY < 0 ? 0 :
            posY < maxPosY ? maxPosY : posY;

        return new Point(posX, posY);
    }

    public killAnimations(): void {
        this.zoomAnimation.kill();
    }
}

export type CameraAreaSize = {
    width: number,
    height: number
}
